import React, {PureComponent, Fragment, ComponentType, createRef} from "react"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import {get, sample, sampleSize, compact, uniq, isNull, map} from "lodash"
import {Grid} from "react-bootstrap"
import styled from "styled-components"
import {InjectedIntl} from "react-intl"

import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import id from "@irati/utils/id"
import ShowPopUpAction from "@irati/store/actions/ShowSuccessPopUp"
import askNicely from "@irati-web/platform/src/utils/askNicely"
import padding from "@irati/ui/src/global/padding"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import tracking from "@irati-tracking/perks/perks"
import authentication from "@irati/auth"
import {DEFAULT_RADIUS_METERS} from "@irati/geolocation/src/constants/proximity"
import {hasValidValues} from "@irati/geolocation/src/helpers/latLng"
import HomePageQueryComponent from "@irati/deals/apollo/query/HomePage"
import LANE_TYPES from "@irati/deals/constants/laneTypes"
import {PERKSGO} from "@irati-web/app/src/routeConfig"
import isBucketLoaded from "@irati/utils/lang/helpers/isBucketLoaded"
import Loading from "@irati/ui/src/Loading"
import {geolocationTracking} from "@irati-tracking/geolocation"
import {getCompanyBenefitsLogo, getBenefitsSchemeHeader} from "@irati/deals/helpers/deals/appearance"
import dealSettingsService from "@irati/deals/services/perksSettings"
import {updatePrivateUserMetadata, getPrivateUserMetadata} from "@irati/user-metadata/services/UserMetadata"
import reorderHomepageLanes from "@irati/deals/helpers/deals/reorderHomepageLanes"
import DealsFavourites from "@irati/deals/apollo/query/DealFavourites"
import favouritesService from "@irati/deals/services/favourites"
import {DealsTag, Deal, LaneList, QueryHomepageLanesArgs, UserMetadata, DealCategory} from "@irati/types"
import {DEFAULT_CONFIG} from "@irati/user-metadata/constants/defaultPushConfig"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import OnboardingModal, {OnboardingModalSteps} from "@irati/ui/src/OnboardingModal"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import {walletTracking} from "@irati-tracking/wallet"
import {hangPromise} from "@irati/utils/promise"

import Button from "@irati/ui/src/Button"
import Slider from "@irati/ui/src/Slider"
import {Hero, HeroSliderSettings} from "@irati/ui/src/Hero/Hero"
import Spacing from "@irati/ui/src/Spacing"
import FlexiPointsBanner from "../../components/FlexiPointsBanner"
import FeedSliderGraph, {ITEM_SIZE_SMALL} from "../../components/FeedSlider"
import SectionHeader from "../../components/SectionHeader"
import SpendSectionHeader from "../../components/SectionHeader/SpendSectionHeader"
import EarnSectionHeader from "../../components/SectionHeader/EarnSectionHeader"
import TopCategories from "../../components/TopCategories"
import PerksNearMeCTA from "../../components/PerksNearMeCTA"
import CustomisationBanner from "../../components/CustomisationBanner"
import CustomCategories from "../../components/CustomCategories"
import step1image from "../../images/wallet_onboarding_01.svg"
import step2image from "../../images/wallet_onboarding_02.svg"
import step3image from "../../images/wallet_onboarding_03.svg"
import {StyledHr} from "./styled/HomePage"
import DealSearch from "../../components/DealSearch"

const StyledTopCategories = styled(TopCategories)`
  padding-bottom: calc(${padding.base} - 10px);
`

const CUSTOMISATION_OPTIONS_TO_DISPLAY = 3

type UserLocation = {
  latLng: {
    lat: number,
    lng: number
  },
  city: string,
  load: Promise<void>,
  error: any
}

type ShowPopUpOptions = {
  show: boolean
}

type AdditionalProps = {
  SectionHeader?: ComponentType,
  tag?: DealsTag,
  showDealLocation?: boolean
}

interface Props extends InjectIntl, WithAuth {
  showPopUp: (options: ShowPopUpOptions) => void,
  userLocation: UserLocation,
  intl: InjectedIntl
}

interface State {
  perksNearMeLaneTrackingSent: boolean,
  isPerksGoBucketLoaded: boolean,
  companyBenefitsTopOfHomepage: boolean,
  metadata: UserMetadata | null,
  hasFetchedMetadata: boolean,
  showTreatTokensOnboardingModal: boolean,
  locationLoad: Promise<any> | null
}

interface ReduxProps {
  mq: any,
  geoLocation: any
}

interface RenderLaneProps {
  type: string,
  tag: DealsTag,
  deals: Deal[],
  headerText: string,
  bodyText: string,
  idNamespace: string,
  index: number,
  image: string,
  linkedEntityUrl: string
}

class HomePage extends PureComponent<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  constructor(props: Props) {
    super(props)
    const {intl} = props
    this.state = {
      perksNearMeLaneTrackingSent: false,
      /*
        @todo: Remove this once we have got rid of react-intl
        react-intl has a bug when using the LangLoader on a bucket that isn't loaded yet -
        it rips out all components and causes them to be re-mounted causing the constructor to be called twice.
        This approach checks that the PerksGO bucket is loaded first as the FeedSlider component has logic
        that would throw an error when a component is unmounted immediately after mount
      */
      isPerksGoBucketLoaded: !authentication.apps("platform.fidel") || isBucketLoaded(intl, PERKSGO),
      companyBenefitsTopOfHomepage: false,
      metadata: null,
      hasFetchedMetadata: false,
      showTreatTokensOnboardingModal: false,
      locationLoad: hangPromise()
    }
  }

  componentDidMount() {
    // force an update to favourites on load of page
    const {
      auth: {user}
    } = this.props
    // What is this call doing here? async refresh apollo cache?
    favouritesService.list({userId: user.uuid}, {fetchPolicy: "network-only"})

    this.getSettings()
    this.getUserMetadata()
    this.setPerksNearMeLaneTracking()

    tracking.viewHome()
    askNicely.setup()

    onboardingService.get(authentication.user().uuid)
      .then((userOnboarding) => {
        const showTreatTokensOnboardingModal =
          authentication.apps("wallet.treatTokens") &&
          userOnboarding.userProgress.filter(({step}) => {
            return step === OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED
          }).length === 0

        this.setState({showTreatTokensOnboardingModal})
      })

    this.fetchLocation()
  }

  componentWillUnmount() {
    this.props.showPopUp({show: false})
  }

  componentDidUpdate() {
    this.setPerksNearMeLaneTracking()
  }

  fetchLocation = async() => {
    const load = this.props.userLocation.load || Promise.resolve()
    this.setState({locationLoad: load})
    await load
    this.setState({locationLoad: null})
  }

  getSettings = () => {
    const {
      auth: {userTenant}
    } = this.props
    const {uuid} = userTenant

    dealSettingsService.get(uuid).then((dealSettings) => {
      const companyBenefitsTopOfHomepage = get(dealSettings, "companyBenefits.topOfHomepage", false)
      this.setState({companyBenefitsTopOfHomepage})
    })
  }

  getUserMetadata = () => {
    const {
      auth: {user}
    } = this.props
    const {uuid} = user
    return getPrivateUserMetadata(uuid, {forceRefetch: true}).then((metadata) => {
      this.setState({metadata, hasFetchedMetadata: true})
    })
  }

  removeUnavailableCategories = (unavailableCategories: DealsTag[]) => {
    const {
      auth: {user}
    } = this.props
    const {uuid: userUuid} = user
    const {metadata} = this.state
    const config = metadata?.config || DEFAULT_CONFIG
    const unavailableCategoryIds = map(unavailableCategories, "uuid")
    const newFavouriteCategories = get(metadata, "config.favouriteCategories", [])
      .filter(({uuid}: DealsTag) => !unavailableCategoryIds.includes(uuid))

    return updatePrivateUserMetadata({
      ...metadata,
      config: {
        ...config,
        favouriteCategoryIds: map(newFavouriteCategories, "uuid")
      },
      uuid: userUuid
    }).then(() => {
      this.getUserMetadata()
    })
  }

  getHeaderName(type: string, tag?: DealsTag, customHeaderName?: string) {
    const {
      formatMessage,
      auth: {userTenant}
    } = this.props
    const benefitSchemeHeader = getBenefitsSchemeHeader()

    switch (type) {
      case LANE_TYPES.FAVOURITE:
        return formatMessage({id: "perks.containers.homepage.favourites"})
      case LANE_TYPES.COMPANY:
        return benefitSchemeHeader || formatMessage({id: "perks.containers.homepage.benefits"}, {name: userTenant.public_name})
      case LANE_TYPES.NEW:
        return formatMessage({id: "perks.containers.homepage.newPerks"})
      case LANE_TYPES.TRENDING:
        return formatMessage({id: "perks.containers.homepage.trending"})
      case LANE_TYPES.SPEND:
        return formatMessage({id: "perks.containers.homepage.spend"})
      case LANE_TYPES.EARN:
        return customHeaderName
      case LANE_TYPES.OTHER:
        return formatMessage({id: customHeaderName})
      case LANE_TYPES.TAG:
        return tag ? tag.name : null
      default:
        return null
    }
  }

  handleSearchAllByLocationCTAClick = () => {
    geolocationTracking.clickPerksHomepagePerksNearMeCTA(this.props.userLocation.city)
  }

  setPerksNearMeLaneTracking = () => {
    const {
      latLng: {lat, lng},
      city
    } = this.props.userLocation
    if (hasValidValues(lat, lng) && !this.state.perksNearMeLaneTrackingSent) {
      this.setState({perksNearMeLaneTrackingSent: true})
      geolocationTracking.viewPerksHomepagePerksNearMeLane(city)
    }
  }

  getAdditionalProps = (type: string, tag: DealsTag) => {
    const additionalProps: AdditionalProps = {}
    if (type === LANE_TYPES.SPEND) {
      additionalProps.SectionHeader = SpendSectionHeader
      additionalProps.tag = tag
    } else if (type === LANE_TYPES.EARN) {
      additionalProps.SectionHeader = EarnSectionHeader
      additionalProps.tag = tag
      additionalProps.showDealLocation = true
    }
    return additionalProps
  }

  renderSlider = ({type, tag, deals, headerText, bodyText, linkedEntityUrl, idNamespace}: Partial<RenderLaneProps>) => {
    const {auth: {branding}} = this.props
    const companyBenefitsLogo = getCompanyBenefitsLogo(branding)
    const additionalProps = this.getAdditionalProps(type, tag)
    const sectionHeaderText = this.getHeaderName(type, tag, headerText)

    return (
      <FeedSliderGraph
        data={deals}
        sectionHeaderText={sectionHeaderText}
        showSectionHeader={!!sectionHeaderText}
        linkedEntityUrl={linkedEntityUrl}
        sectionBodyText={bodyText}
        id={id(type, idNamespace)}
        logo={type === LANE_TYPES.COMPANY ? companyBenefitsLogo : null}
        {...additionalProps} />
    )
  }

  renderLane = ({type, tag, deals, headerText, bodyText, linkedEntityUrl, idNamespace, image, index}: RenderLaneProps) => {
    // @todo: Remove feature flag check once live
    if (
      (type === LANE_TYPES.SPEND && !authentication.apps("wallet.users")) ||
      (type === LANE_TYPES.EARN && (!authentication.apps("wallet.users") || !authentication.apps("perks.perksGo")))
    ) {
      return null
    }

    let Component
    let key

    if (type === LANE_TYPES.NEAR_ME) {
      Component = (
        <>
          <PerksNearMeCTA
            id={id("perks-near-me-cta", idNamespace)}
            userLocation={this.props.userLocation}
            headerText={headerText}
            bodyText={bodyText}
            image={image} />
          {deals && this.renderSlider({type, tag, deals, headerText, bodyText, idNamespace, image, linkedEntityUrl, index})}
        </>
      )
    } else if (deals.length > 0) {
      key = tag ? tag.uuid : index
      Component = this.renderSlider({type, tag, deals, headerText, bodyText, idNamespace, image, linkedEntityUrl, index})
    }

    if (Component) {
      return (
        <Fragment key={key || index}>
          <StyledHr />
          {Component}
        </Fragment>
      )
    }

    return null
  }

  orderHomepageLanes = (homepageLanes: LaneList, idNamespace: string) => {
    return reorderHomepageLanes(homepageLanes.results, this.state.companyBenefitsTopOfHomepage).map(
      ({type, tag, deals, headerText, bodyText, image, linkedEntityUrl}, index) =>
        this.renderLane({type, tag, deals, headerText, bodyText, idNamespace, image, linkedEntityUrl, index})
    )
  }

  getBannerSuggestions = (homepageDealCategories: DealCategory[]) => {
    const randomNames = compact(homepageDealCategories)
      .filter(({active, type}) => active === true && isNull(type))
      .map(({tags}) => sample(tags)?.name)
    return sampleSize(compact(uniq(randomNames)), CUSTOMISATION_OPTIONS_TO_DISPLAY)
  }

  getWalletContext() {
    const {apps} = this.context
    return apps.WALLET
  }

  onboardingModalRef = createRef()
  onboardingModalSettings = () => [
    {
      image: step1image,
      title: "perks.onboarding.step1.title",
      description: "perks.onboarding.step1.description"
    },
    {
      image: step2image,
      title: "perks.onboarding.step2.title",
      description: "perks.onboarding.step2.description"
    },
    {
      image: step3image,
      title: "perks.onboarding.step3.title",
      description: "perks.onboarding.step3.description",
      actions: [
        <ButtonRouterLink
          marginRight
          app={this.getWalletContext()}
          onClick={() => {
            onboardingService.completeStep(authentication.user(), OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED)
            walletTracking.clickGoToMyWallet()
            this.onboardingModalRef.current.hideModal()
          }}
          to="/">
          <FormattedMessage id="perks.onboarding.finalStep.buttons.goToMyWallet" />
        </ButtonRouterLink>,
        <Button
          primary
          onClick={() => {
            onboardingService.completeStep(authentication.user(), OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED)
            walletTracking.clickDone(this.props.auth.userTenant.uuid)
            this.onboardingModalRef.current.hideModal()
          }}>
          <FormattedMessage id="perks.onboarding.finalStep.buttons.done" />
        </Button>
      ]
    }
  ] as OnboardingModalSteps[]

  render() {
    const {userLocation, formatMessage, auth} = this.props
    const {user} = auth
    const idNamespace = id("homepage", this.props, {force: true})
    /**  containers should force to init the idNamespace */

    const {isPerksGoBucketLoaded, metadata, hasFetchedMetadata, showTreatTokensOnboardingModal, locationLoad} = this.state
    if (locationLoad) {
      return <Loading load={locationLoad} />
    }

    const {
      latLng: {lat, lng}
    } = userLocation
    const variables: Partial<QueryHomepageLanesArgs> = {}
    if (hasValidValues(lat, lng)) {
      variables.lat = lat
      variables.lng = lng
      variables.radius = DEFAULT_RADIUS_METERS
    }

    return (
      <HomePageQueryComponent variables={variables}>
        {({data: {homepageBanner, homepageDealCategories, homepageLanes}}) => (
          <>
            <DealSearch />
            <Grid>
              <Spacing marginTop={4} />
              <Slider slidesToShow={1} settings={HeroSliderSettings}>
                {homepageBanner
                  ? [homepageBanner].flat().map((deal, i) =>
                    (<Hero
                      key={deal.uuid}
                      {...deal}
                      link={`/info/${deal.urlSlug}`}
                      onClick={() => tracking.clickPerk(deal, {sectionHeader: "hero", position: i + 1, source: "homepage"})} />))
                  : null}
              </Slider>

              <FlexiPointsBanner />
              {(user && hasFetchedMetadata) && <CustomisationBanner
                user={user}
                metadata={metadata}
                suggestions={this.getBannerSuggestions(homepageDealCategories?.results)} />}
              {metadata && (
                <CustomCategories
                  idNamespace={id("custom-categories", idNamespace)}
                  metadata={metadata}
                  removeUnavailableCategories={this.removeUnavailableCategories} />)}
              <SectionHeader
                id={id("categories", idNamespace)}
                linkLabel={formatMessage({id: "perks.containers.homepage.topCategories.label"})}
                linkTo="/categories"
                onClick={tracking.clickBrowseByCategory}>
                <FormattedMessage id="perks.containers.homepage.topCategories.title" />
              </SectionHeader>
              {homepageDealCategories && <StyledTopCategories id={idNamespace} categories={homepageDealCategories.results} />}
              {isPerksGoBucketLoaded && (
                <DealsFavourites variables={{userId: user.uuid}}>
                  {({data}) => (
                    <FeedSliderGraph
                      id={id("favourites", idNamespace)}
                      data={data?.dealFavourites}
                      sectionHeaderText={this.getHeaderName(LANE_TYPES.FAVOURITE)}
                      showSectionHeader={!!data?.dealFavourites?.length}
                      itemSize={ITEM_SIZE_SMALL}
                      isFavouriteDeal />
                  )}
                </DealsFavourites>
              )}
              {homepageLanes && this.orderHomepageLanes(homepageLanes, idNamespace)}
              {showTreatTokensOnboardingModal &&
              <OnboardingModal steps={this.onboardingModalSettings()} ref={this.onboardingModalRef} />}
            </Grid>
          </>
        )}
      </HomePageQueryComponent>
    )
  }
}

const mapStateToProps = ({mq, geoLocation}: ReduxProps) => ({mq, userLocation: geoLocation.userLocation})

/**
 * @param dispatch
 */
const mapDispatchToProps = (dispatch: (action: any) => void) => ({
  showPopUp: (options: ShowPopUpOptions) => dispatch(ShowPopUpAction(options))
})

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withAuth(HomePage))
)
